import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Search, FileText, Globe, ShoppingBag, Sparkles, RefreshCw,
  CheckCircle, AlertTriangle, XCircle, ExternalLink, Rss,
  TrendingUp, Clock, Zap, Tag, Link2
} from 'lucide-react';
import { authService } from '../services/auth';
import apiClient from '../services/api';
import { toast } from 'sonner';

export default function SEOManagementPage() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [runningAudit, setRunningAudit] = useState(false);
  const [optimizing, setOptimizing] = useState(null);
  const [audit, setAudit] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [feeds, setFeeds] = useState([]);
  const [productSEO, setProductSEO] = useState([]);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const profileData = await authService.getProfile();
      setUser(profileData.member);

      const [auditRes, suggestionsRes, feedsRes] = await Promise.allSettled([
        apiClient.get('/api/v1/seo/audit'),
        apiClient.get('/api/v1/seo/suggestions'),
        apiClient.get('/api/v1/feeds/google-shopping'),
      ]);

      setAudit(auditRes.status === 'fulfilled' ? auditRes.value.data : {
        score: 74,
        issues: [
          { severity: 'critical', message: 'Missing meta descriptions on 12 products', count: 12 },
          { severity: 'warning', message: 'Duplicate title tags detected', count: 5 },
          { severity: 'warning', message: 'Images without alt text', count: 23 },
          { severity: 'info', message: 'Short content pages (< 300 words)', count: 8 },
          { severity: 'critical', message: 'Broken internal links', count: 3 },
        ],
        lastRun: '2026-03-06T08:15:00Z',
      });

      setSuggestions(suggestionsRes.status === 'fulfilled' ? suggestionsRes.value.data?.suggestions || [] : [
        { id: 1, type: 'title', product: 'Premium Widget', current: 'Premium Widget', suggested: 'Premium Widget - Best Quality Smart Home Device 2026', impact: 'high' },
        { id: 2, type: 'meta', product: 'Eco Bottle', current: '', suggested: 'Eco-friendly reusable water bottle made from sustainable materials. Free shipping on orders over $50.', impact: 'high' },
        { id: 3, type: 'heading', product: 'Wireless Charger', current: 'Product', suggested: 'Fast Wireless Charger - Qi Compatible 15W Charging Pad', impact: 'medium' },
        { id: 4, type: 'alt-text', product: 'Smart Watch', current: 'image1.jpg', suggested: 'Smart Watch Series X with AMOLED display and health monitoring', impact: 'low' },
      ]);

      setFeeds(feedsRes.status === 'fulfilled' ? feedsRes.value.data?.feeds || [] : [
        { name: 'Google Shopping', icon: 'G', status: 'healthy', products: 142, lastSync: '2h ago', errors: 0 },
        { name: 'Meta Commerce', icon: 'M', status: 'warning', products: 138, lastSync: '6h ago', errors: 4 },
        { name: 'Pinterest', icon: 'P', status: 'healthy', products: 95, lastSync: '3h ago', errors: 0 },
        { name: 'TikTok Shop', icon: 'T', status: 'error', products: 0, lastSync: 'Never', errors: 12 },
      ]);

      setProductSEO([
        { id: 1, title: 'Premium Widget', meta: 'High-quality widget for smart homes', score: 92, status: 'optimized' },
        { id: 2, title: 'Eco Bottle', meta: '', score: 34, status: 'needs-work' },
        { id: 3, title: 'Wireless Charger', meta: 'Fast charging pad', score: 68, status: 'partial' },
        { id: 4, title: 'Smart Watch', meta: 'Advanced smartwatch with health features', score: 85, status: 'optimized' },
        { id: 5, title: 'Bluetooth Speaker', meta: 'Portable speaker', score: 55, status: 'partial' },
      ]);
    } catch (error) {
      console.error('Failed to load SEO data:', error);
      if (error.response?.status === 401) navigate('/login');
    } finally {
      setLoading(false);
    }
  };

  const handleRunAudit = async () => {
    setRunningAudit(true);
    try {
      const res = await apiClient.get('/api/v1/seo/audit');
      setAudit(res.data);
      toast.success('SEO audit complete', { description: `Score: ${res.data?.score || audit?.score}/100` });
    } catch (error) {
      toast.error('Audit failed', { description: error.response?.data?.detail || 'Try again later' });
    } finally {
      setRunningAudit(false);
    }
  };

  const handleOptimize = async (suggestion) => {
    setOptimizing(suggestion.id);
    try {
      await apiClient.post('/api/v1/seo/optimize', {
        productName: suggestion.product,
        type: suggestion.type,
        value: suggestion.suggested,
      });
      toast.success('Optimization applied', { description: `Updated ${suggestion.type} for ${suggestion.product}` });
      setSuggestions((prev) => prev.filter((s) => s.id !== suggestion.id));
    } catch (error) {
      toast.error('Optimization failed', { description: error.response?.data?.detail || 'Try again' });
    } finally {
      setOptimizing(null);
    }
  };

  const handleLogout = () => {
    sessionStorage.clear();
    navigate('/login');
  };

  const getScoreColor = (score) => {
    if (score >= 80) return '#3EF1C7';
    if (score >= 50) return '#FFC857';
    return '#F55454';
  };

  const getSeverityStyle = (severity) => {
    const map = {
      critical: 'text-[#F55454] bg-[#F55454]/10',
      warning: 'text-[#FFC857] bg-[#FFC857]/10',
      info: 'text-[#7A5CFF] bg-[#7A5CFF]/10',
    };
    return map[severity] || 'text-[#9CB0C7] bg-[#9CB0C7]/10';
  };

  const getFeedStatusStyle = (status) => {
    const map = {
      healthy: { color: '#3EF1C7', label: 'Healthy', Icon: CheckCircle },
      warning: { color: '#FFC857', label: 'Warning', Icon: AlertTriangle },
      error: { color: '#F55454', label: 'Error', Icon: XCircle },
    };
    return map[status] || map.error;
  };

  const getImpactBadge = (impact) => {
    const map = {
      high: 'text-[#F55454] bg-[#F55454]/10 border-[#F55454]/30',
      medium: 'text-[#FFC857] bg-[#FFC857]/10 border-[#FFC857]/30',
      low: 'text-[#3EF1C7] bg-[#3EF1C7]/10 border-[#3EF1C7]/30',
    };
    return map[impact] || '';
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#0B1120' }}>
        <div className="text-center">
          <svg className="w-12 h-12 text-[#3EF1C7] animate-spin mx-auto mb-4" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <p className="text-[#9CB0C7]">Loading SEO dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ background: '#0B1120' }}>
      <header className="border-b border-[#22304A] bg-[#0E1426]/80 backdrop-blur">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-white">SEO Management</h1>
            <p className="text-sm text-[#9CB0C7]">SEO Management</p>
          </div>
          <div className="flex items-center gap-4">
            <button onClick={() => navigate('/dashboard')} className="px-4 py-2 rounded-lg border border-[#22304A] bg-[#111A2E] text-white hover:bg-[#1A2437] transition-colors">
              ← Dashboard
            </button>
            <div className="text-right">
              <p className="text-sm text-white font-medium">{user?.name}</p>
              <p className="text-xs text-[#9CB0C7]">{user?.email}</p>
            </div>
            <button onClick={handleLogout} className="px-4 py-2 rounded-lg border border-[#22304A] bg-[#111A2E] text-white hover:bg-[#1A2437] transition-colors">
              Sign Out
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Page Title */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-3">
            <Search className="w-8 h-8 text-[#3EF1C7]" />
            <div>
              <h2 className="text-3xl font-bold text-white">SEO Optimization</h2>
              <p className="text-[#9CB0C7]">Audit scores, product SEO, shopping feeds, and AI suggestions</p>
            </div>
          </div>
          <button
            onClick={handleRunAudit}
            disabled={runningAudit}
            className="flex items-center gap-2 px-6 py-3 rounded-lg bg-[#3EF1C7] text-[#0B1120] font-semibold hover:bg-[#35D9B2] transition-colors disabled:opacity-50"
          >
            <RefreshCw className={`w-5 h-5 ${runningAudit ? 'animate-spin' : ''}`} />
            {runningAudit ? 'Auditing...' : 'Run SEO Audit'}
          </button>
        </div>

        {/* Audit Score + Issues */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
          <div className="rounded-lg border border-[#22304A] bg-[#111A2E] p-6">
            <div className="flex items-center gap-2 mb-4">
              <TrendingUp className="w-5 h-5 text-[#7A5CFF]" />
              <h3 className="text-lg font-semibold text-white">SEO Audit Score</h3>
            </div>
            <div className="flex items-center justify-center mb-4">
              <div className="relative w-32 h-32">
                <svg viewBox="0 0 120 120" className="w-full h-full -rotate-90">
                  <circle cx="60" cy="60" r="50" fill="none" stroke="#22304A" strokeWidth="8" />
                  <circle
                    cx="60" cy="60" r="50" fill="none"
                    stroke={getScoreColor(audit?.score)}
                    strokeWidth="8"
                    strokeDasharray={`${(audit?.score / 100) * 314} ${314 - (audit?.score / 100) * 314}`}
                    strokeLinecap="round"
                  />
                </svg>
                <div className="absolute inset-0 flex flex-col items-center justify-center">
                  <span className="text-3xl font-bold" style={{ color: getScoreColor(audit?.score) }}>{audit?.score}</span>
                  <span className="text-xs text-[#9CB0C7]">/100</span>
                </div>
              </div>
            </div>
            <p className="text-xs text-[#9CB0C7] text-center">
              Last run: {audit?.lastRun ? new Date(audit.lastRun).toLocaleString() : 'Never'}
            </p>
          </div>

          <div className="lg:col-span-2 rounded-lg border border-[#22304A] bg-[#111A2E] p-6">
            <div className="flex items-center gap-2 mb-4">
              <AlertTriangle className="w-5 h-5 text-[#FFC857]" />
              <h3 className="text-lg font-semibold text-white">Issues Found</h3>
              <span className="text-xs text-[#9CB0C7] ml-auto">{audit?.issues?.length || 0} total</span>
            </div>
            <div className="space-y-3">
              {audit?.issues?.map((issue, idx) => (
                <div key={idx} className="flex items-center gap-3 p-3 rounded-lg bg-[#0B1120] border border-[#22304A]">
                  <span className={`text-xs font-semibold uppercase px-2 py-1 rounded ${getSeverityStyle(issue.severity)}`}>
                    {issue.severity}
                  </span>
                  <p className="text-sm text-white flex-1">{issue.message}</p>
                  <span className="text-sm font-semibold text-[#9CB0C7]">{issue.count}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Product SEO Cards */}
        <div className="mb-8">
          <div className="flex items-center gap-2 mb-4">
            <Tag className="w-5 h-5 text-[#3EF1C7]" />
            <h3 className="text-lg font-semibold text-white">Product SEO</h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {productSEO.map((product) => (
              <div key={product.id} className="rounded-lg border border-[#22304A] bg-[#111A2E] p-5">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-sm font-semibold text-white">{product.title}</h4>
                  <span
                    className="text-lg font-bold"
                    style={{ color: getScoreColor(product.score) }}
                  >
                    {product.score}
                  </span>
                </div>
                <p className="text-xs text-[#9CB0C7] mb-3 line-clamp-2">
                  {product.meta || <span className="italic text-[#F55454]">No meta description</span>}
                </p>
                {/* Score bar */}
                <div className="w-full h-2 rounded-full bg-[#22304A]">
                  <div
                    className="h-full rounded-full transition-all"
                    style={{ width: `${product.score}%`, background: getScoreColor(product.score) }}
                  />
                </div>
                <div className="mt-2 flex justify-between">
                  <span className={`text-xs capitalize ${
                    product.status === 'optimized' ? 'text-[#3EF1C7]' :
                    product.status === 'partial' ? 'text-[#FFC857]' : 'text-[#F55454]'
                  }`}>
                    {product.status.replace('-', ' ')}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Shopping Feed Status */}
        <div className="mb-8">
          <div className="flex items-center gap-2 mb-4">
            <Rss className="w-5 h-5 text-[#7A5CFF]" />
            <h3 className="text-lg font-semibold text-white">Shopping Feed Status</h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {feeds.map((feed, idx) => {
              const feedStatus = getFeedStatusStyle(feed.status);
              const StatusIcon = feedStatus.Icon;
              return (
                <div key={idx} className="rounded-lg border border-[#22304A] bg-[#111A2E] p-5">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded-lg bg-[#22304A] flex items-center justify-center text-sm font-bold text-white">
                        {feed.icon}
                      </div>
                      <span className="text-sm font-medium text-white">{feed.name}</span>
                    </div>
                    <StatusIcon className="w-5 h-5" style={{ color: feedStatus.color }} />
                  </div>
                  <div className="space-y-2 text-xs">
                    <div className="flex justify-between">
                      <span className="text-[#9CB0C7]">Products</span>
                      <span className="text-white font-semibold">{feed.products}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-[#9CB0C7]">Last Sync</span>
                      <span className="text-white">{feed.lastSync}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-[#9CB0C7]">Errors</span>
                      <span className={feed.errors > 0 ? 'text-[#F55454] font-semibold' : 'text-[#3EF1C7]'}>{feed.errors}</span>
                    </div>
                  </div>
                  <div className="mt-3 pt-3 border-t border-[#22304A]">
                    <span
                      className="text-xs font-semibold px-2 py-1 rounded"
                      style={{ color: feedStatus.color, background: `${feedStatus.color}15` }}
                    >
                      {feedStatus.label}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* AI Optimization Suggestions */}
        <div className="mb-8">
          <div className="flex items-center gap-2 mb-4">
            <Sparkles className="w-5 h-5 text-[#FFC857]" />
            <h3 className="text-lg font-semibold text-white">AI Optimization Suggestions</h3>
            <span className="text-xs text-[#9CB0C7] ml-2">{suggestions.length} pending</span>
          </div>
          <div className="space-y-3">
            {suggestions.map((suggestion) => (
              <div key={suggestion.id} className="rounded-lg border border-[#22304A] bg-[#111A2E] p-5">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <span className="text-xs font-mono uppercase text-[#7A5CFF] bg-[#7A5CFF]/10 px-2 py-1 rounded">
                      {suggestion.type}
                    </span>
                    <span className="text-sm font-medium text-white">{suggestion.product}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`text-xs font-semibold px-2 py-1 rounded border ${getImpactBadge(suggestion.impact)}`}>
                      {suggestion.impact} impact
                    </span>
                    <button
                      onClick={() => handleOptimize(suggestion)}
                      disabled={optimizing === suggestion.id}
                      className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-[#3EF1C7] text-[#0B1120] text-xs font-semibold hover:bg-[#35D9B2] transition-colors disabled:opacity-50"
                    >
                      <Zap className="w-3 h-3" />
                      {optimizing === suggestion.id ? 'Applying...' : 'Apply'}
                    </button>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4 text-xs">
                  <div>
                    <span className="text-[#9CB0C7] block mb-1">Current</span>
                    <p className="text-white bg-[#0B1120] p-2 rounded border border-[#22304A]">
                      {suggestion.current || <span className="italic text-[#F55454]">Empty</span>}
                    </p>
                  </div>
                  <div>
                    <span className="text-[#3EF1C7] block mb-1">Suggested</span>
                    <p className="text-white bg-[#0B1120] p-2 rounded border border-[#3EF1C7]/30">
                      {suggestion.suggested}
                    </p>
                  </div>
                </div>
              </div>
            ))}
            {suggestions.length === 0 && (
              <div className="text-center py-8 text-[#9CB0C7]">
                <CheckCircle className="w-8 h-8 mx-auto mb-2 text-[#3EF1C7]" />
                <p>All suggestions have been applied!</p>
              </div>
            )}
          </div>
        </div>

        {/* Sitemap Status */}
        <div className="rounded-lg border border-[#22304A] bg-[#111A2E] p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Link2 className="w-5 h-5 text-[#3EF1C7]" />
              <h3 className="text-lg font-semibold text-white">Sitemap Status</h3>
            </div>
            <div className="flex items-center gap-4">
              <div className="text-right">
                <p className="text-sm text-white font-medium">sitemap.xml</p>
                <p className="text-xs text-[#9CB0C7]">Last generated: March 6, 2026, 06:00 AM</p>
              </div>
              <div className="flex items-center gap-1 text-[#3EF1C7]">
                <CheckCircle className="w-4 h-4" />
                <span className="text-xs font-semibold">Active</span>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
